import type React from 'react'
import { forwardRef } from 'react'
import type { AgentAttachment } from '@multiplayer-app/ai-agent-types'
import { useComposerDraft } from '@/hooks/useComposerDraft'
import { createContextAttachment, type ContextAttachmentParams } from '@/utils/contextAttachments'

export interface AttachContextButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Attachment (or builder) to add to the current composer draft.
   * Prefer passing a function if you need to compute timestamps/IDs at click time.
   */
  attachment?: AgentAttachment | (() => AgentAttachment | undefined)
  /**
   * Convenience: pass context params and let the library fill `id`, `type`, `schemaVersion`,
   * `capturedAt`, and default `source.url` under the hood.
   *
   * If both `attachment` and `context` are provided, `attachment` wins.
   */
  context?: ContextAttachmentParams | (() => ContextAttachmentParams | undefined)
  /**
   * Called after the attachment is added.
   */
  onAdded?: (attachment: AgentAttachment) => void
}

/**
 * Minimal helper button for host apps to attach context from anywhere.
 * This is intentionally unstyled; bring your own UI.
 */
export const AttachContextButton = forwardRef<HTMLButtonElement, AttachContextButtonProps>(
  ({ attachment, context, onAdded, onClick, ...buttonProps }, ref) => {
    const { addAttachments } = useComposerDraft()

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      onClick?.(event)
      if (event.defaultPrevented) return

      const resolved = typeof attachment === 'function' ? attachment() : attachment

      const built =
        resolved ??
        (() => {
          const resolvedCtx = typeof context === 'function' ? context() : context
          return resolvedCtx ? createContextAttachment(resolvedCtx) : undefined
        })()

      if (!built) return

      addAttachments([built])
      onAdded?.(built)
    }

    return <button ref={ref} type='button' {...buttonProps} onClick={handleClick} />
  }
)

AttachContextButton.displayName = 'AttachContextButton'
